/**
 * O unico caminho pelo qual esta PWA muda alguma coisa no servidor.
 *
 * Responsabilidade: chamar o `api.ts`, traduzir falha em aviso legivel e
 * mandar a sessao perdida para o `sessao.ts`.
 *
 * NAO e responsabilidade deste arquivo: validar comando. A gramatica das cinco
 * categorias vive em `api/app/schemas/command.py` e responde 422 com o motivo
 * em texto; repeti-la aqui criaria uma segunda gramatica que divergiria da
 * primeira — o mesmo erro que a regra da mutacao unica evita no firmware.
 *
 * ## Por que toda view passa por aqui
 *
 * Sem um lugar so, cada botao trataria erro do seu jeito: um mostraria
 * `[object Object]`, outro engoliria a falha, e um terceiro deslogaria o
 * usuario por um 503 do broker. `executar()` da o mesmo comportamento a todos.
 */

import * as api from "./api";
import { ErroApi, SemRede, SemSessao } from "./api";
import * as sessao from "./sessao";
import * as store from "./store";
import type { CommandIn, PrefsNotificacao } from "./tipos";

/**
 * Roda `fn`, devolve `true` se deu certo, e deixa um aviso na tela se nao.
 *
 * `rotulo` e o que o usuario tentou fazer, em portugues — vira o comeco da
 * mensagem de erro, para o aviso dizer o que falhou e nao so por que.
 */
export async function executar<T>(
  rotulo: string,
  fn: () => Promise<T>,
  { quietoSemRede = false } = {},
): Promise<T | null> {
  store.avisar(null);
  try {
    return await fn();
  } catch (erro) {
    if (erro instanceof SemSessao) {
      sessao.encerrar();
      return null;
    }
    if (erro instanceof SemRede) {
      // Leitura que falha offline nao merece aviso proprio: a barra de estado
      // ja diz, uma vez e para a tela inteira, que nao ha conexao e de quando
      // e o dado. Um segundo aviso dizendo o mesmo — e, pior, dizendo "nada
      // foi enviado" sobre um GET — so ensina o usuario a ignorar avisos.
      if (!quietoSemRede) {
        store.avisar(`${rotulo}: sem conexão com a nuvem — nada foi enviado.`);
      }
      return null;
    }
    if (erro instanceof ErroApi) {
      store.avisar(
        erro.status === 503
          ? `${rotulo}: o broker está fora do ar, o comando não foi enviado.`
          : `${rotulo}: ${erro.detalhe}`,
      );
      return null;
    }
    store.avisar(`${rotulo}: falhou.`);
    return null;
  }
}

/**
 * Publica um comando no aparelho.
 *
 * `201` significa "publicado no broker em QoS1", nao "executado" — o desfecho
 * chega depois, pelo ack, como um segundo quadro `command` no relay
 * (`app/schemas/ws.py::WsCommand`). Por isso esta funcao nao mostra sucesso: o
 * feed de comandos mostra, quando o ack chegar.
 */
export async function comandar(
  deviceId: string,
  rotulo: string,
  corpo: CommandIn,
): Promise<void> {
  const criado = await executar(rotulo, () => api.enviarComando(deviceId, corpo));
  if (criado !== null) store.aplicarComando(deviceId, criado);
}

/**
 * Quanto tempo a tela espera a foto antes de desistir.
 *
 * Uma captura ponta a ponta e a soma de: publish QoS1, dois `fb_get()` no
 * aparelho (`fb_count = 1` obriga a descartar o quadro velho), `upload/request`
 * -> `grant`, o PUT HTTPS, e o `upload/done` voltando como quadro do relay.
 * Medido no painel local, so a captura leva ~1,5 s; 25 s da folga para um
 * Wi-Fi ruim sem deixar o botao preso para sempre quando o aparelho nao
 * responde.
 */
const ESPERA_CAPTURA_MS = 25_000;

/** Marca "pedindo" antes de o ULID existir. Nunca casa com um nome de objeto. */
const AGUARDANDO_ULID = "";

const relogios = new Map<string, number>();

/**
 * Pede uma foto agora (contrato §6, `cmd: "snapshot"`).
 *
 * O `201` aqui significa menos do que em qualquer outro comando: quer dizer
 * "publicado no broker", e o ack que vem depois quer dizer so "vou capturar".
 * A IMAGEM chega por um terceiro caminho — um quadro `upload` do relay, com o
 * ULID deste comando no nome do objeto (§7). Por isso o estado de "capturando"
 * guarda o ULID: e `store.aplicarUpload()` que o apaga, ao reconhecer a foto
 * daquele pedido, e nao esta funcao.
 *
 * O relogio existe para o caso em que a foto nunca chega — aparelho sem
 * camera que aceitou o comando, PUT que falhou, link que caiu no meio. Sem
 * ele o botao ficaria "Capturando…" para sempre.
 */
export async function capturarAgora(deviceId: string): Promise<void> {
  if (store.obter().capturando[deviceId] != null) return;

  store.definirCaptura(deviceId, AGUARDANDO_ULID);
  const criado = await executar("capturar foto", () =>
    api.enviarComando(deviceId, { cmd: "snapshot", key: "", value: "" }),
  );
  if (criado === null) {
    // 429 (outra captura ha pouco), 503 (broker fora), 404. `executar()` ja
    // pos o motivo na barra de aviso; aqui so solta o botao.
    store.definirCaptura(deviceId, null);
    return;
  }

  store.aplicarComando(deviceId, criado);
  store.definirCaptura(deviceId, criado.id);

  window.clearTimeout(relogios.get(deviceId));
  relogios.set(
    deviceId,
    window.setTimeout(() => {
      relogios.delete(deviceId);
      // So desiste se a captura pendente ainda for ESTA: uma foto que chegou
      // ja zerou o estado, e um pedido novo feito depois nao pode ser
      // cancelado pelo relogio do anterior.
      if (store.obter().capturando[deviceId] !== criado.id) return;
      store.definirCaptura(deviceId, null);
      store.avisar("A foto não chegou. O aparelho pode estar sem conexão.");
    }, ESPERA_CAPTURA_MS),
  );
}

/**
 * Quanto tempo a tela espera o aparelho dar sinal depois de um `ota`.
 *
 * Ao contrario do `snapshot`, aqui nao ha um artefato chegando: o que fecha a
 * espera e o proprio aparelho publicando o bloco `ota` do estado retido (§5).
 * O caminho ate la e `command` QoS1 -> `firmware/request` -> `grant` (o
 * `OTA_GRANT_TIMEOUT_MS` do `gfc` da 20 s para o grant chegar) -> primeiro
 * byte do GET. Dois minutos cobrem isso com folga em Wi-Fi ruim.
 *
 * O que este relogio NAO mede e o download: quem o reporta e o aparelho, com
 * `pct`, e ai a tela ja saiu deste estado. Ele so cobre o caso de o aparelho
 * nunca ter pegado o comando — offline, ou o EMQX guardando a mensagem para
 * quando ele voltar, o que pode levar dias.
 */
const ESPERA_OTA_MS = 120_000;

const relogiosOta = new Map<string, number>();

/**
 * Manda instalar uma versao (contrato §6, `cmd: "ota"`).
 *
 * O `201` daqui quer dizer "publicado no broker", e o ack que vem depois quer
 * dizer so "vou baixar" — exatamente como o do `snapshot` diz "vou capturar".
 * O desfecho chega por um terceiro caminho: o bloco `ota` do estado retido,
 * com `baixando`/`gravando`/`pendente`, e depois do reboot o `fw` do
 * `state/online`.
 *
 * **A nuvem nao esta na cadeia de confianca da imagem.** Se a imagem nao for
 * assinada pela chave de release, o aparelho a recusa no `esp_ota_end()` e
 * continua na versao antiga — nada nesta funcao, nem em nenhuma outra deste
 * repositorio, pode mudar isso.
 */
export async function atualizarFirmware(
  deviceId: string,
  versao: string,
): Promise<void> {
  if (store.obter().atualizando[deviceId] != null) return;

  store.definirAtualizacao(deviceId, versao);
  const criado = await executar("atualizar o firmware", () =>
    api.enviarComando(deviceId, { cmd: "ota", key: versao, value: "" }),
  );
  if (criado === null) {
    // 429 (outra atualizacao ha pouco), 503 (broker fora), 404, 422.
    // `executar()` ja pos o motivo na barra de aviso; aqui so solta o botao.
    store.definirAtualizacao(deviceId, null);
    return;
  }

  store.aplicarComando(deviceId, criado);

  window.clearTimeout(relogiosOta.get(deviceId));
  relogiosOta.set(
    deviceId,
    window.setTimeout(() => {
      relogiosOta.delete(deviceId);
      // So desiste se a espera ainda for por ESTA versao: um aparelho que ja
      // comecou zerou o estado, e um pedido novo nao pode ser cancelado pelo
      // relogio do anterior.
      if (store.obter().atualizando[deviceId] !== versao) return;
      store.definirAtualizacao(deviceId, null);
      store.avisar(
        "O aparelho ainda não começou a atualização. Se ele estiver sem " +
          "conexão, o comando fica guardado e vale quando ele voltar.",
      );
    }, ESPERA_OTA_MS),
  );
}

/**
 * Carrega o detalhe do aparelho toda vez que a selecao mudar.
 *
 * Existe porque o relay entrega o SNAPSHOT (aparelhos + digital twin) mas nao
 * o historico: comandos, a serie de telemetria e — por `acompanharTimelapse()`
 * — os frames vem por REST, e o relay so manda os que acontecerem com a aba ja
 * aberta. Sem este acompanhamento, o primeiro
 * aparelho — o que o snapshot seleciona sozinho, sem ninguem clicar — abriria
 * com o timelapse vazio ate o usuario clicar em outro e voltar. Foi assim que
 * o gate da C9 encontrou o buraco.
 *
 * Um lugar so, e nao um `carregarDetalhes()` espalhado por cada view que muda
 * a selecao: a regra da mutacao unica aplicada a um efeito.
 */
export function acompanharSelecao(): void {
  let anterior: string | null = null;
  store.assinar(() => {
    const atual = store.obter().selecionado;
    if (atual === anterior || atual === null) return;
    anterior = atual;
    void carregarDetalhes(atual);
  });
}

/**
 * Busca a serie do periodo aberto, uma vez por combinacao de aparelho+periodo.
 *
 * Trocar de metrica nao refaz o GET: as quatro grandezas vem na mesma amostra
 * (§4), entao a serie que ja esta na memoria serve para todas. O que dispara
 * um GET novo e abrir o grafico numa chave (`store.chaveSerie`) que ainda nao
 * tem serie — inclusive reabrir depois de trocar de periodo, ou trocar de
 * periodo com o grafico ja aberto.
 *
 * Mesma disciplina do `acompanharSelecao()`: o efeito mora num lugar so, e nao
 * dentro da view que por acaso foi clicada.
 */
export function acompanharGrafico(): void {
  let anterior: string | null = null;
  store.assinar(() => {
    const estado = store.obter();
    const alvo =
      estado.metricaAberta === null || estado.selecionado === null
        ? null
        : estado.selecionado;
    const chave = alvo === null ? null : store.chaveSerie(alvo, estado.periodoGrafico);
    if (chave === anterior) return;
    anterior = chave;
    if (alvo === null || chave === null) return;
    if (estado.serie !== null && estado.serie.chave === chave) return;
    void carregarHistorico(alvo, estado.periodoGrafico);
  });
}

/**
 * Refaz o GET de uploads quando o aparelho OU o periodo do timelapse mudam.
 *
 * O periodo e a janela do GET (`since`/`until`), entao trocar de janela e
 * trocar de resposta — nao da para filtrar no cliente o que nunca foi baixado.
 * Fica aqui, e nao dentro da view, pelo mesmo motivo de `acompanharGrafico()`:
 * assim o `store.uploads` tem UMA origem, e trocar de aparelho com "30 dias"
 * escolhido nao volta calado para 24 h.
 *
 * E por isso que `carregarDetalhes()` nao busca uploads: eram duas origens
 * para a mesma lista, e a segunda chegava depois, sobrescrevendo a janela
 * pedida com a janela padrao.
 */
export function acompanharTimelapse(): void {
  let anterior: string | null = null;
  store.assinar(() => {
    const estado = store.obter();
    if (estado.selecionado === null) return;
    const chave = `${estado.selecionado}|${JSON.stringify(estado.periodoTimelapse)}`;
    if (chave === anterior) return;
    anterior = chave;
    void carregarUploads(estado.selecionado, estado.periodoTimelapse);
  });
}

export async function carregarUploads(
  deviceId: string,
  periodo: store.PeriodoTimelapse,
): Promise<void> {
  const lista = await executar("carregar timelapse", () => api.uploads(deviceId, janela(periodo)), {
    quietoSemRede: true,
  });
  if (lista !== null) store.definirUploads(deviceId, lista);
}

/**
 * Periodo escolhido -> `since`/`until` do GET.
 *
 * As datas do modo personalizado viram limites em **UTC**, e nao no fuso do
 * navegador, porque o `captured_at` de um frame e a hora do RELOGIO DO
 * APARELHO gravada no nome do arquivo (`YYYY-MM-DD_HH`, §7) e lida como UTC
 * pela nuvem. Escolher "01/08" tem que trazer os frames que o proprio aparelho
 * chamou de 01/08 — que e o mesmo recorte por texto que o painel local faz
 * (`gfc/core/timelapse_index.cpp::timelapse_id_in_range`). Converter pelo fuso
 * do celular deslocaria a janela em algumas horas, e o dono da estufa veria o
 * dia errado por estar viajando.
 *
 * A janela relativa nao tem ponta superior de proposito: com o aparelho num
 * fuso a leste de UTC, o frame mais novo chega rotulado no "futuro", e um
 * `until = agora` o esconderia justamente da lista que abre em 24 h.
 */
function janela(periodo: store.PeriodoTimelapse): api.JanelaUploads {
  if (periodo.modo === "relativo") {
    if (periodo.dias === null) return {};
    return { desde: new Date(Date.now() - periodo.dias * 86_400_000).toISOString() };
  }
  const recorte: api.JanelaUploads = {};
  if (periodo.de !== "") recorte.desde = `${periodo.de}T00:00:00Z`;
  if (periodo.ate !== "") recorte.ate = `${periodo.ate}T23:59:59Z`;
  return recorte;
}

/**
 * Acima disto a janela nao cabe crua no teto do endpoint (5000 amostras): o
 * servidor agrega em baldes de min/max. 2000 e nao 5000 porque o teto do
 * endpoint conta LINHAS, e uma resposta agregada tem ate duas por balde — a
 * folga evita bater no teto quando a janela crua fica bem no limite.
 */
const AMOSTRAS_CRUAS_MAX = 2000;

/**
 * Baldes pedidos ao servidor quando a janela e agregada. `views/grafico.ts`
 * ainda reduz a serie para 100 pontos depois disto — este teto e so o que
 * evita pedir uma resposta enorme, nao o que decide a resolucao do desenho.
 */
const BALDES = 700;

/** `undefined` quando a janela cabe crua; senao o tamanho de balde, em segundos. */
function bucketPara(janelaSegundos: number): number | undefined {
  if (janelaSegundos / 60 <= AMOSTRAS_CRUAS_MAX) return undefined;
  return Math.max(60, Math.ceil(janelaSegundos / BALDES));
}

/**
 * Periodo do grafico -> `since`/`until`/`bucket` do GET.
 *
 * Ao contrario de `janela()` do timelapse (acima), as datas do modo
 * personalizado aqui viram limites no FUSO DO NAVEGADOR, e nao em UTC:
 * `ts` da telemetria e um instante real — `timestamptz` no banco, epoch no
 * fio (contrato §4) —, e nao um recorte por texto sobre o nome de um arquivo
 * gravado pelo relogio do aparelho. Quem escolhe "19/08 08:00" quer as 8 da
 * manha DELE, e nao as 8 da manha em Greenwich.
 */
function janelaGrafico(periodo: store.PeriodoGrafico): api.JanelaSerie {
  if (periodo.modo === "relativo") {
    const segundos = periodo.horas * 3600;
    const recorte: api.JanelaSerie = {
      desde: new Date(Date.now() - segundos * 1000).toISOString(),
    };
    const bucket = bucketPara(segundos);
    if (bucket !== undefined) recorte.bucket = bucket;
    return recorte;
  }
  const recorte: api.JanelaSerie = {};
  if (periodo.de !== "") recorte.desde = new Date(periodo.de).toISOString();
  if (periodo.ate !== "") recorte.ate = new Date(periodo.ate).toISOString();
  if (recorte.desde !== undefined && recorte.ate !== undefined) {
    const segundos = (Date.parse(recorte.ate) - Date.parse(recorte.desde)) / 1000;
    const bucket = bucketPara(segundos);
    if (bucket !== undefined) recorte.bucket = bucket;
  }
  return recorte;
}

export async function carregarHistorico(
  deviceId: string,
  periodo: store.PeriodoGrafico,
): Promise<void> {
  const chave = store.chaveSerie(deviceId, periodo);
  const amostras = await executar(
    "carregar o período",
    () => api.serie(deviceId, janelaGrafico(periodo)),
    { quietoSemRede: true },
  );
  if (amostras === null) return;

  // O usuario pode ter trocado de aparelho OU de periodo enquanto o GET
  // estava no ar; uma resposta atrasada da chave ANTERIOR nao pode sobrescrever
  // a serie que a tela esta mostrando agora.
  const atual = store.obter();
  if (atual.selecionado === null) return;
  if (store.chaveSerie(atual.selecionado, atual.periodoGrafico) !== chave) return;

  store.definirSerie(chave, amostras);
}

export async function carregarDetalhes(deviceId: string): Promise<void> {
  // Frames de TIMELAPSE nao entram aqui: quem os busca e
  // `acompanharTimelapse()`, que tambem dispara nesta mesma troca de aparelho,
  // mas conhece o periodo. Snapshot entra: ele nao tem periodo nenhum — sao os
  // mais recentes e ponto —, entao nao ha segundo dono possivel.
  const [comandos, telemetria, snapshots, releases] = await Promise.all([
    executar("carregar comandos", () => api.comandos(deviceId), { quietoSemRede: true }),
    executar("carregar histórico", () => api.telemetria(deviceId), {
      quietoSemRede: true,
    }),
    executar("carregar fotos", () => api.uploads(deviceId, {}, "snapshot"), {
      quietoSemRede: true,
    }),
    // Entra aqui e nao num acompanhamento proprio: nao tem periodo nem janela,
    // e a resposta depende so do aparelho — a mesma razao dos snapshots.
    executar("carregar atualizações", () => api.firmware(deviceId), {
      quietoSemRede: true,
    }),
  ]);
  if (comandos !== null) store.definirComandos(deviceId, comandos);
  if (telemetria !== null) store.definirTelemetria(deviceId, telemetria);
  if (snapshots !== null) store.definirSnapshots(deviceId, snapshots);
  if (releases !== null) store.definirReleases(deviceId, releases);
}

// --- alertas e notificacoes (C12) --------------------------------------

/**
 * Carrega o feed e o contador. Chamado no boot e ao voltar para a aba.
 *
 * O relay entrega o alerta NOVO, um a um, e so os que acontecerem com a aba
 * aberta — exatamente como faz com comandos e uploads. O historico e o badge
 * correto vem daqui.
 */
/**
 * Recarrega o feed e as preferencias toda vez que a conexao VOLTA a ficar ao vivo.
 *
 * Existe por um buraco que o gate da C9 encontrou: `main.ts` so busca as duas
 * coisas no caminho de sucesso do boot, e um boot OFFLINE (modo aviao, celular
 * sem sinal, aba reaberta no metro) sai pelo ramo do `SemRede` antes disso.
 * Quando a rede voltava, o `ws.ts` reconectava sozinho e a tela ficava ao vivo
 * — com o feed de alertas vazio e os interruptores de notificacao travados,
 * para sempre, ate um F5. O socket nao ajuda: ele entrega o alerta NOVO, nunca
 * o historico.
 *
 * Dispara na BORDA de subida, e nao a cada mudanca: `conectando` -> `ao-vivo`
 * acontece a cada reconexao, e sem a borda um Wi-Fi instavel refaria os dois
 * GET a cada oscilacao.
 *
 * Mesma disciplina de `acompanharSelecao()`: o efeito mora num lugar so.
 */
export function acompanharConexao(): void {
  let anterior: store.EstadoConexao | null = null;
  store.assinar(() => {
    const atual = store.obter().conexao;
    if (atual === anterior) return;
    const subiu = atual === "ao-vivo" && anterior !== "ao-vivo";
    anterior = atual;
    if (!subiu) return;
    void carregarAlertas();
    void carregarPrefsNotificacao();
  });
}

export async function carregarAlertas(): Promise<void> {
  const pagina = await executar("carregar alertas", () => api.alertas(), {
    quietoSemRede: true,
  });
  if (pagina !== null) store.definirAlertas(pagina.alerts, pagina.nao_lidos);
}

/**
 * Marca como lido ate o alerta mais novo que a tela JA desenhou.
 *
 * O corte e o `created_at` do topo da lista, e nao "agora": um alarme que
 * chegue entre o clique e o commit tem que continuar contando como nao-lido.
 * Sem isso, o unico modo de falha que um canal de alerta nao pode ter — o de
 * apagar sozinho um aviso que ninguem viu — seria uma corrida de milissegundos.
 */
export async function marcarAlertasLidos(): Promise<void> {
  const lista = store.obter().alertas;
  if (lista.length === 0 || store.obter().naoLidos === 0) return;
  const corte = lista[0]?.created_at;
  if (corte === undefined) return;
  const pagina = await executar("marcar como lido", () => api.marcarAlertasLidos(corte), {
    quietoSemRede: true,
  });
  if (pagina !== null) store.marcarAlertasLidos(pagina.nao_lidos, corte);
}

export async function carregarPrefsNotificacao(): Promise<void> {
  const prefs = await executar("carregar notificações", () => api.prefsNotificacao(), {
    quietoSemRede: true,
  });
  if (prefs !== null) store.definirPrefsNotificacao(prefs);
}

/**
 * Liga/desliga um tipo de alerta. Otimista, com desfazer em caso de falha.
 *
 * Otimista porque um interruptor que so se move depois do round-trip parece
 * quebrado num celular em 3G; e com desfazer porque um interruptor que ficou
 * ligado sem ter gravado e pior do que um lento — ele mentiria sobre estar
 * recebendo alarme.
 */
export async function alternarTipoDeAlerta(
  tipo: keyof PrefsNotificacao,
  ligado: boolean,
): Promise<void> {
  const atuais = store.obter().prefsNotificacao;
  if (atuais === null) return;
  const novas = { ...atuais, [tipo]: ligado };
  store.definirPrefsNotificacao(novas);
  const gravadas = await executar("salvar notificações", () =>
    api.gravarPrefsNotificacao(novas),
  );
  store.definirPrefsNotificacao(gravadas ?? atuais);
}
